import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Star, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import OptionsComparison from "@/components/OptionsComparison";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { track } from "@/lib/analytics";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "€0",
    period: "forever",
    features: [
      "5 incidents logged per month",
      "Big Five personality assessment",
      "Leader D3 assessment",
      "One full AI advisor conversation",
      "Vulnerability report (summary)",
    ],
    limitations: ["Further AI advisor conversations locked", "Personalised counter-strategies locked"],
  },
  {
    id: "premium",
    name: "Premium",
    monthly: { id: "premium_monthly", price: "€19", period: "per month" },
    annual: { id: "premium_annual", price: "€149", period: "per year · save €79" },
    popular: true,
    features: [
      "Unlimited incident logging",
      "Full vulnerability report with strategies",
      "Personalised counter-strategies",
      "Escalation risk alerts",
      "Trend analytics",
      "Unlimited AI advisor conversations",
    ],
  },
  {
    id: "consulting",
    name: "1-on-1 Session",
    price: "€299",
    period: "per session",
    features: [
      "Live 60-minute session with a Bad Boss Clinic behavioural scientist",
      "Expert consultation on your case",
      "Personalised coaching",
      "Follow-up summary document",
      "Written case file available as a €175 add-on",
    ],
    booking: true,
  },
];

export default function PricingPage() {
  const { user } = usePortalAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [openingBilling, setOpeningBilling] = useState(false);
  const [period, setPeriod] = useState("monthly");

  useEffect(() => {
    track("pricing_viewed", { tier: user?.tier || "unknown" });
  }, [user?.tier]);

  const checkout = async (plan) => {
    setLoadingPlan(plan);
    track("subscription_checkout_started", { plan });
    try {
      const { checkout_url } = await portalApi.checkout(plan, window.location.origin);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.message);
      setLoadingPlan(null);
    }
  };

  const manageBilling = async () => {
    setOpeningBilling(true);
    try {
      const { portal_url } = await portalApi.manageBilling();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(err.message);
      setOpeningBilling(false);
    }
  };

  const currentTier = user?.tier || "free";
  const canManageBilling = currentTier !== "free" || user?.has_billing;

  // Arriving from a "Go Premium" CTA (e.g. the landing page) with ?checkout=<plan> takes the
  // client straight to Stripe once, so they never dead-end after signing in.
  const [searchParams] = useSearchParams();
  const autoFired = useRef(false);
  useEffect(() => {
    const plan = searchParams.get("checkout");
    const valid = ["premium_monthly", "premium_annual", "strategy_report"];
    if (autoFired.current || !user) return;
    if (plan && valid.includes(plan) && currentTier !== "premium") {
      autoFired.current = true;
      checkout(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, currentTier]);

  return (
    <PortalLayout>
      <div data-testid="pricing-page">
        <div className="mb-8 max-w-2xl">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Plans</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">Choose how much support you want</h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            Start free. Upgrade when you need the tactics, not just the diagnosis. Cancel any time.
          </p>
        </div>

        <div className="inline-flex border border-slate-800 mb-10" data-testid="billing-period-toggle">
          {[
            { key: "monthly", label: "Monthly" },
            { key: "annual", label: "Yearly · save 35%" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => {
                setPeriod(opt.key);
                track("billing_period_toggled", { period: opt.key });
              }}
              data-testid={`billing-period-${opt.key}`}
              className={`px-6 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                period === opt.key ? "bg-[#D4AF37] text-slate-950 font-medium" : "text-slate-400 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {canManageBilling && (
          <div
            className="border border-slate-800 bg-[#0B1220]/70 p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="billing-self-service"
          >
            <div>
              <p className="text-white text-sm mb-1">
                You're on {currentTier === "premium" ? "Premium" : currentTier.replace("_", " ")}
              </p>
              <p className="text-slate-500 text-sm">
                Update your card, download invoices or cancel — no email required.
              </p>
            </div>
            <button
              onClick={manageBilling}
              disabled={openingBilling}
              data-testid="manage-billing-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 disabled:opacity-50 transition-colors"
            >
              {openingBilling ? <Loader2 className="animate-spin" size={13} /> : <CreditCard size={13} />}
              {openingBilling ? "Opening" : "Manage billing"}
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((plan, idx) => {
            const variant = plan.monthly ? (period === "annual" ? plan.annual : plan.monthly) : plan;
            const checkoutId = plan.monthly ? variant.id : plan.id;
            const isCurrent = plan.id === currentTier || (plan.id === "premium" && currentTier === "premium");
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative border p-7 flex flex-col ${
                  plan.popular ? "border-[#D4AF37] bg-[#D4AF37]/[0.06]" : "border-slate-800 bg-[#0B1220]/70"
                }`}
                style={{ animation: `fadeInUp 0.4s ease-out ${idx * 80}ms both` }}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-7 bg-[#D4AF37] text-slate-950 text-[10px] uppercase tracking-[0.2em] px-3 py-1 flex items-center gap-1.5">
                    <Star size={10} /> Most chosen
                  </span>
                )}
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">{plan.name}</p>
                <p className="font-serif text-3xl text-white mb-1" data-testid={`plan-price-${plan.id}`}>{variant.price}</p>
                <p className="text-xs text-slate-500 mb-7">{variant.period}</p>

                <ul className="space-y-3 mb-7 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-slate-300 text-sm leading-snug">
                      <Check size={14} className="text-[#D4AF37] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                  {plan.limitations?.map((f) => (
                    <li key={f} className="flex gap-2.5 text-slate-600 text-sm leading-snug">
                      <span className="shrink-0 mt-0.5">×</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {plan.id === "free" ? (
                  <span
                    className="text-center border border-slate-800 text-slate-500 py-3.5 text-[11px] uppercase tracking-[0.2em]"
                    data-testid="plan-free-state"
                  >
                    {currentTier === "free" ? "Your current plan" : "Included"}
                  </span>
                ) : plan.booking ? (
                  <Link
                    to="/"
                    data-testid="plan-book-session-btn"
                    className="text-center border border-[#D4AF37] text-[#D4AF37] py-3.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
                  >
                    Book a session
                  </Link>
                ) : isCurrent ? (
                  <span className="text-center border border-[#D4AF37] text-[#D4AF37] py-3.5 text-[11px] uppercase tracking-[0.2em]" data-testid={`plan-current-${plan.id}`}>
                    Active
                  </span>
                ) : (
                  <button
                    onClick={() => checkout(checkoutId)}
                    disabled={!!loadingPlan}
                    data-testid={`plan-checkout-${checkoutId}`}
                    className={`flex items-center justify-center gap-2 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium transition-colors disabled:opacity-60 ${
                      plan.popular
                        ? "bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                        : "border border-slate-700 text-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                    }`}
                  >
                    {loadingPlan === checkoutId && <Loader2 className="animate-spin" size={13} />}
                    {loadingPlan === checkoutId ? "Opening checkout" : "Choose plan"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <h2 className="font-serif text-2xl text-white mb-3">Every option side by side</h2>
          <p className="text-slate-400 text-sm mb-8 max-w-2xl leading-relaxed">
            Portal plans handle the week-to-week. A live session with a behavioural scientist is for the decisions that matter.
          </p>
          <OptionsComparison onBook={() => (window.location.href = "/")} />
        </div>

        <p className="text-slate-600 text-xs mt-9 max-w-2xl leading-relaxed" data-testid="pricing-footnote">
          Payments are processed securely by Stripe on behalf of Apex Advisory — Bad Boss Clinic is a service of Apex
          Advisory, and your card statement shows a discreet reference (LEADERSHIP) rather than the clinic name. Test
          mode is active — use card 4242 4242 4242 4242 with any future expiry date and any CVC.
        </p>
      </div>
    </PortalLayout>
  );
}
